import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faTools,
  faArrowLeft,
  faUpload,
  faFileInvoice,
  faTruck,
  faBell,
} from '@fortawesome/free-solid-svg-icons';
import { Dialog } from 'primereact/dialog';
import { useAppContext } from '../../AppContext';
import UploadPayment from '../Invoice/UploadPayment';
import GenerateNslfModal from './ToolModals/GenerateNslfModal';
import GenerateWtnIcModal from './ToolModals/GenerateWtnIcModal';
import SendNotificationModal from './ToolModals/SendNotificationModal';
import Loader from '../Layout/Loader';
import useSettingsCompany from './useSettingsCompany';
import SettingsBreadcrumb from './SettingsBreadcrumb';
import './settings.scss';

const ToolsSettings = () => {
  const { t } = useTranslation('translations');
  const { socketRef: [socketRef] } = useAppContext();
  const {
    company,
    loading,
    isAdminMode,
    getBackUrl,
    user,
    selectedCompany,
  } = useSettingsCompany();

  const [openUploadPayment, setOpenUploadPayment] = useState(false);
  const [openGenerateNslf, setOpenGenerateNslf] = useState(false);
  const [openGenerateWtnIc, setOpenGenerateWtnIc] = useState(false);
  const [openSendNotification, setOpenSendNotification] = useState(false);

  const userRoleId = user?.role?.id;

  const tools = [
    {
      id: 'upload-payment',
      title: t('navbar.uploadPayment'),
      icon: faUpload,
      onClick: () => setOpenUploadPayment(true),
      allowedRoles: [1, 2, 3, 4, 5],
    },
    {
      id: 'generate-nslf',
      title: t('navbar.generateNslf'),
      icon: faFileInvoice,
      onClick: () => setOpenGenerateNslf(true),
      allowedRoles: [1, 2, 3, 4, 5, 6, 7],
    },
    {
      id: 'generate-wtnic',
      title: t('navbar.generateWtnIc'),
      icon: faTruck,
      onClick: () => setOpenGenerateWtnIc(true),
      allowedRoles: [1, 2, 3, 4, 5, 6, 7],
    },
    {
      id: 'send-notification',
      title: t('navbar.sendNotification'),
      icon: faBell,
      onClick: () => setOpenSendNotification(true),
      allowedRoles: [1],
    },
  ];

  const visibleTools = tools.filter((tool) =>
    tool.allowedRoles.includes(userRoleId)
  );

  if (loading || !company) {
    return <Loader />;
  }

  return (
    <>
      <Dialog
        header={t('navbar.uploadPayment')}
        visible={openUploadPayment}
        onHide={() => setOpenUploadPayment(false)}
        style={{ width: '26vw' }}
        breakpoints={{ '960px': '75vw', '641px': '100vw' }}
      >
        <UploadPayment
          user={user}
          selectedCompany={selectedCompany}
          fullCompany={company}
        />
      </Dialog>

      <Dialog
        header={t('navbar.generateNslf')}
        visible={openGenerateNslf}
        onHide={() => setOpenGenerateNslf(false)}
        style={{ width: '50vw' }}
        breakpoints={{ '960px': '75vw', '641px': '100vw' }}
      >
        <GenerateNslfModal
          user={user}
          selectedCompany={selectedCompany}
          onSuccess={() => setOpenGenerateNslf(false)}
        />
      </Dialog>

      <Dialog
        header={t('navbar.generateWtnIc')}
        visible={openGenerateWtnIc}
        onHide={() => setOpenGenerateWtnIc(false)}
        style={{ width: '50vw' }}
        breakpoints={{ '960px': '75vw', '641px': '100vw' }}
      >
        <GenerateWtnIcModal
          user={user}
          selectedCompany={selectedCompany}
          onSuccess={() => setOpenGenerateWtnIc(false)}
        />
      </Dialog>

      <Dialog
        header={t('navbar.sendNotification')}
        visible={openSendNotification}
        onHide={() => setOpenSendNotification(false)}
        style={{ width: '50vw' }}
        contentStyle={{ overflow: 'visible' }}
        breakpoints={{ '960px': '75vw', '641px': '100vw' }}
      >
        <SendNotificationModal
          user={user}
          socketRef={socketRef}
          onSuccess={() => setOpenSendNotification(false)}
        />
      </Dialog>

      <div className="settings-subpage">
        <SettingsBreadcrumb
          items={[{ label: t('settings.tools') }]}
          company={company}
          isAdminMode={isAdminMode}
        />

        <div className="settings-subpage__header">
          <Link to={getBackUrl()} className="settings-subpage__back-btn">
            <FontAwesomeIcon icon={faArrowLeft} />
          </Link>
          <h2 className="settings-subpage__title">
            <span className="settings-subpage__icon">
              <FontAwesomeIcon icon={faTools} />
            </span>
            {t('settings.tools')}
          </h2>
          {company && (
            <span className="settings-subpage__company-badge">
              {company.name}
            </span>
          )}
        </div>

        <div className="tools-grid">
          {visibleTools.map((tool) => (
            <div
              key={tool.id}
              className="tool-card"
              onClick={tool.onClick}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  tool.onClick?.();
                }
              }}
            >
              <div className="tool-card__icon">
                <FontAwesomeIcon icon={tool.icon} />
              </div>
              <h4 className="tool-card__title">{tool.title}</h4>
            </div>
          ))}
        </div>
      </div>
    </>
  );
};

export default ToolsSettings;
